import React, { useState, useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Outlet } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { AuthProvider } from "../context/Auth/AuthContext";
import { WhatsAppsProvider } from "../context/WhatsApp/WhatsAppsContext";
import LoggedInLayout from "../layout";
import BackdropLoading from "../components/BackdropLoading";
import ForgotPassword from "../pages/ForgotPassword";
import Login from "../pages/Login/";
import ResetPassword from "../pages/ResetPassword";
import Signup from "../pages/Signup/";
import PrivateRoute from "./PrivateRoute";
import AdminRoute from "./AdminRoute";
import StrictAdminRoute from "./StrictAdminRoute";
import MasterAdminRoute from "./MasterAdminRoute";
import PublicRoute from "./PublicRoute";

// Rotas privadas carregadas sob demanda: cada usuário só baixa o código
// das páginas que efetivamente acessa, em vez do bundle completo no boot
// (inclusive antes do login).
const Api = lazy(() => import("../pages/Api/"));
const ApiDocs = lazy(() => import("../pages/ApiDocs/"));
const ApiKey = lazy(() => import("../pages/ApiKey/"));
const Documentation = lazy(() => import("../pages/Documentation/"));
const Runbook = lazy(() => import("../pages/Runbook/"));
const Channels = lazy(() => import("../pages/Channels/"));
const Contacts = lazy(() => import("../pages/Contacts/"));
const BlockedContacts = lazy(() => import("../pages/BlockedContacts/"));
const CpuUsage = lazy(() => import("../pages/CpuUsage/"));
const Dashboard = lazy(() => import("../pages/Dashboard/"));
const VersionCheck = lazy(() => import("../pages/VersionCheck/"));
const DiskSpace = lazy(() => import("../pages/DiskSpace/"));
const MemoryUsage = lazy(() => import("../pages/MemoryUsage/"));
const ErrorLogs = lazy(() => import("../pages/ErrorLogs/"));
const FileManager = lazy(() => import("../pages/FileManager/"));
const Queues = lazy(() => import("../pages/Queues/"));
const QuickAnswers = lazy(() => import("../pages/QuickAnswers/"));
const Settings = lazy(() => import("../pages/Settings/"));
const Backup = lazy(() => import("../pages/Backup"));
const ActivityLogs = lazy(() => import("../pages/ActivityLogs"));
const NetworkStatus = lazy(() => import("../pages/NetworkStatus"));
const QueueMonitor = lazy(() => import("../pages/QueueMonitor"));
const UserMonitor = lazy(() => import("../pages/UserMonitor"));
const DatabaseStatus = lazy(() => import("../pages/DatabaseStatus"));
const SystemHealth = lazy(() => import("../pages/SystemHealth"));
const WhatsLab = lazy(() => import("../pages/WhatsLab"));
const SystemUpdate = lazy(() => import("../pages/SystemUpdate"));
const SystemControls = lazy(() => import("../pages/SystemControls"));
const Tags = lazy(() => import("../pages/Tags"));
const ClientStatus = lazy(() => import("../pages/ClientStatus"));
const Tickets = lazy(() => import("../pages/Tickets/"));
const Users = lazy(() => import("../pages/Users"));
const Protocols = lazy(() => import("../pages/Protocols/"));
const Notes = lazy(() => import("../pages/Notes/"));
const Videos = lazy(() => import("../pages/Videos"));
const HealthCheck = lazy(() => import("../pages/HealthCheck"));
const GroupManagement = lazy(() => import("../pages/GroupManagement"));

const AppRoutes = ({ toggleTheme, onThemeConfigUpdate }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 600);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<PublicRoute element={<Login />} />} />
          <Route path="/signup" element={<PublicRoute element={<Signup />} />} />
          <Route path="/forgot-password" element={<PublicRoute element={<ForgotPassword />} />} />
          <Route path="/reset-password" element={<PublicRoute element={<ResetPassword />} />} />
          
          <Route path="/" element={
            <PrivateRoute element={
              <WhatsAppsProvider>
                <LoggedInLayout toggleTheme={toggleTheme} onThemeConfigUpdate={onThemeConfigUpdate}>
                  <Suspense fallback={<BackdropLoading />}>
                    <Outlet />
                  </Suspense>
                </LoggedInLayout>
              </WhatsAppsProvider>
            } />
          }>
            <Route index element={<Dashboard />} />
            <Route path="documentation" element={<Documentation />} />
            <Route path="runbook" element={<Runbook />} />
            <Route path="tickets/:ticketId?" element={<Tickets />} />
            <Route path="protocols" element={<Protocols />} />
            <Route path="notes" element={<Notes />} />
            <Route path="channels" element={<Channels />} />
            <Route path="contacts" element={<Contacts />} />
            <Route path="blocked-contacts" element={<AdminRoute element={<BlockedContacts />} />} />
            <Route path="users" element={<Users />} />
            <Route path="quickAnswers" element={<QuickAnswers />} />
            <Route path="Settings" element={<Settings toggleTheme={toggleTheme} onThemeConfigUpdate={onThemeConfigUpdate} />} />
            <Route path="api" element={<Api />} />
            <Route path="apidocs" element={<ApiDocs />} />
            <Route path="apikey" element={<ApiKey />} />
            <Route path="Queues" element={<Queues />} />
            <Route path="Tags" element={<Tags />} />
            <Route path="ClientStatus" element={<ClientStatus />} />
            <Route path="errorLogs" element={<ErrorLogs />} />
            <Route path="diskSpace" element={<DiskSpace />} />
            <Route path="file-manager" element={<FileManager />} />
            <Route path="memoryUsage" element={<MemoryUsage />} />
            <Route path="cpuUsage" element={<CpuUsage />} />
            <Route path="backup" element={<Backup />} />
            <Route path="activity-logs" element={<ActivityLogs />} />
            <Route path="network-status" element={<NetworkStatus />} />
            <Route path="queue-monitor" element={<QueueMonitor />} />
            <Route path="user-monitor" element={<UserMonitor />} />
            <Route path="databaseStatus" element={<DatabaseStatus />} />
            <Route path="system-health" element={<SystemHealth />} />
            <Route path="system-controls" element={<StrictAdminRoute element={<SystemControls />} />} />
            <Route path="system-update" element={<MasterAdminRoute element={<SystemUpdate />} />} />
            <Route path="versionCheck" element={<MasterAdminRoute element={<VersionCheck />} />} />
            <Route path="videos" element={<Videos />} />
            <Route path="health-check" element={<HealthCheck />} />
            <Route path="whatslab" element={<WhatsLab />} />
            <Route path="group-management" element={<GroupManagement />} />
          </Route>
        </Routes>
        <ToastContainer 
          position={isMobile ? "bottom-center" : "top-right"}
          autoClose={3000}
          hideProgressBar={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
      </AuthProvider>
    </BrowserRouter>
  );
};

export default AppRoutes;